import { getServiceRequestAssignee } from '@/components/Kanban/ServiceRequests/Calendar/utils';
import FEATURE_FLAGS from '@/constants/featureFlags';
import LOCAL_FEATURE_FLAGS from '@/constants/localFeatureFlags';
import PERMISSIONS from '@/constants/permissions';
import * as JourneysSelectors from '@/selectors/journeySelectors';
import * as PreferencesSelector from '@/selectors/preferencesSelectors';
import { ServiceRequestModel } from '@/typings/models/ServiceRequest';
import { PartnerCallTrackingSettings } from '@/typings/models/User';
import { countryFromLocale } from '@/utils/parsePhone';
import {
  hasAccess,
  hasFeatureFlag,
  hasMarketingDataAccess,
} from '@/utils/access';
import {
  ParsedQueryParameters,
  parseQueryParametersForFilters,
  ParseQueryParametersOptions,
} from '@/utils/functions';
import { INTEGRATION_TYPE_CARAT } from '@/utils/integrations';
import { isMimicSession } from '@/services/auth';
import { Tooltip } from 'antd';
import isEmpty from 'lodash/isEmpty';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useLocation, useModel, useSelector } from 'umi';

export const usePreviouslySelectedSolution = () => {
  const { initialState, setInitialState } = useModel('@@initialState');
  const isPreferencesInitialized = useSelector(
    PreferencesSelector.isPreferencesListInitialized,
  );
  let previouslySelectedSolution = useSelector(
    PreferencesSelector.selectSelectedSolution,
  );

  useEffect(() => {
    if (previouslySelectedSolution && isPreferencesInitialized) {
      previouslySelectedSolution = initialState.solutions?.includes(
        previouslySelectedSolution,
      )
        ? previouslySelectedSolution
        : initialState.solutions?.[0] || null;
      if (!previouslySelectedSolution) return;

      setInitialState({
        ...initialState,
        selectedSolution: previouslySelectedSolution,
      });
    }
  }, [isPreferencesInitialized]);
};

export function useOverflowTooltip(content) {
  const ref = useRef(null);
  const [isOverflowed, setIsOverflowed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (el) {
      setIsOverflowed(el.scrollWidth > el.clientWidth);
    }
  }, [content]);

  const Element = (
    <div
      ref={ref}
      className="truncate-text"
      style={{
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '100%',
      }}
    >
      {content}
    </div>
  );

  return isOverflowed ? <Tooltip title={content}>{Element}</Tooltip> : Element;
}

export const useQueryParams = () => {
  const location = useLocation();
  const [queryObject, setQueryObject] = useState<Record<string, string>>({});
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const newQueryObject: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      newQueryObject[key] = value;
    });
    setQueryObject(newQueryObject);
  }, [location.search]);
  return queryObject;
};

export const useHasMarketingDataAccess = () => {
  const { initialState } = useModel('@@initialState');

  return hasMarketingDataAccess(initialState.currentUser);
};
export const useHasSalesInfluencersAccess = () => {
  const { initialState } = useModel('@@initialState');

  return hasAccess(initialState.currentUser, [
    PERMISSIONS.SalesInfluencers.VIEW,
    PERMISSIONS.BUSINESS_PARTNER,
  ]);
};
/**
 * Is `type` installed and switched on for the partner?
 *
 * Reads `/me`, which carries the full list already — so no request, and no
 * `integrations_view` permission, which most agent roles do not have (asking
 * `GET /integrations` for them answers 403). `GET /integrations` names the type
 * `type` and returns only active rows; `/me` names it `integration_type` and
 * returns every row with an `is_active` flag. Both shapes reach this hook.
 *
 * `preferences.userIntegrations` wins when it is loaded: the integrations pages
 * refetch it after an install, and `/me` is only read at boot.
 */
export const useHasActiveIntegration = (type: string) => {
  const fromMe = usePartnerIntegrations();
  const fromModel = useSelector(
    PreferencesSelector.selectPartnerUserIntegrations,
  );
  // The preferences model seeds `userIntegrations` with `false`, not undefined,
  // so anything that is not an array means "not loaded".
  const integrations = Array.isArray(fromModel) ? fromModel : fromMe;

  return (integrations || []).some(
    (integration: any) =>
      (integration?.integration_type === type || integration?.type === type) &&
      integration?.is_active !== false,
  );
};

/**
 * Is the Carat planning surface available (CLICKUP-86cb5zayy)? It needs both
 * the Deal Manager module and an installed Carat app — the Back Office hides
 * the lead-details card entirely when either is missing.
 */
export const useHasCaratAccess = () => {
  const hasDealManager = useHasFeatureFlag(FEATURE_FLAGS.DEAL_MANAGER);
  const hasCarat = useHasActiveIntegration(INTEGRATION_TYPE_CARAT);

  return hasDealManager && hasCarat;
};

// Installs belong to the Automation & AI module; support may set one up from a mimic session.
export const useCanInstallIntegrations = () =>
  useHasFeatureFlag(FEATURE_FLAGS.AI_AUTOMATION) || isMimicSession();

export const useHasPartnerConfigEditAccess = () => {
  const { initialState } = useModel('@@initialState');

  return hasAccess(initialState.currentUser, [
    PERMISSIONS.PartnerConfig.EDIT,
    PERMISSIONS.BUSINESS_PARTNER,
  ]);
};

export const useCustomerRequiredFields = () => {
  const { initialState } = useModel('@@initialState');

  return initialState?.currentUser?.Partner?.customer_required_fields || [];
};

// Default country for phone inputs: national numbers are read against it.
export const usePartnerPhoneCountry = () => {
  const { initialState } = useModel('@@initialState');
  return countryFromLocale(initialState?.currentUser?.Partner?.locale);
};

export const usePartnerCallTrackingSettings =
  (): PartnerCallTrackingSettings => {
    const { initialState } = useModel('@@initialState');
    return initialState.currentUser?.Partner?.call_tracking_settings || {};
  };

export const useHasAccess = (
  access: string | string[] | readonly string[],
  addBusinessPartner = true,
) => {
  const { initialState } = useModel('@@initialState');
  const requested = Array.isArray(access) ? access : [access as string];

  // A copy, never `push` on the argument: callers pass shared constants
  // (`DRAWER_ACCESS`, roleMapping entries), and appending to one of those grew
  // it by a `'+*'` on every render and leaked the bypass into other call sites.
  const required =
    addBusinessPartner && !requested.includes(PERMISSIONS.BUSINESS_PARTNER)
      ? [...requested, PERMISSIONS.BUSINESS_PARTNER]
      : [...requested];
  return hasAccess(initialState.currentUser, required);
};

export const useHasFeatureFlag = (flags: string | string[]) => {
  const { initialState } = useModel('@@initialState');
  return hasFeatureFlag(initialState.currentUser, flags);
};

/**
 * Goal sections on the dashboards ("My goal", the Sales Cockpit goal card)
 * are part of the Deal Manager module, like Goal Planning itself — some
 * partners must not see revenue metrics without it. The Laravel GoalPlanPolicy
 * enforces the same flag.
 */
export const useDashboardGoalsEnabled = () =>
  useHasFeatureFlag(FEATURE_FLAGS.DEAL_MANAGER) && LOCAL_FEATURE_FLAGS.DASHBOARD_GOALS;

export const useDidMountEffect = (func, deps) => {
  const didMount = useRef(false);

  useEffect(() => {
    if (didMount.current) func();
    else didMount.current = true;
  }, deps);
};

export const useAlertBeforeUnload = (hasPendingChanges) => {
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = ''; // required for most browsers to show the dialog
    };

    if (hasPendingChanges) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasPendingChanges]);
};

/**
 * Custom hook for parsing query parameters and converting them to filter payload
 * Automatically handles location changes and provides React integration
 *
 * @param place - The filter place identifier for Redux state
 * @param options - Configuration options for parameter mapping and exclusions
 * @returns Parsed query parameters with filter payload and metadata that updates on location change
 */
export const useQueryParametersForFilters = (
  place: string,
  options: ParseQueryParametersOptions = {},
): ParsedQueryParameters => {
  const location = useLocation();
  const [parsedParams, setParsedParams] = useState<ParsedQueryParameters>({
    hasFilterParams: false,
    filterPayload: {},
    shouldClearSavedView: false,
  });

  useEffect(() => {
    const result = parseQueryParametersForFilters(
      location.search,
      place,
      options,
    );
    setParsedParams(result);
  }, [location.search, place, JSON.stringify(options)]);

  return parsedParams;
};

export function usePartnerIntegrations() {
  const { initialState } = useModel('@@initialState');
  const partner = initialState?.currentUser?.Partner;
  return Array.isArray(partner?.integrations) ? partner?.integrations : [];
}

export function useIntegrationsToConnect() {
  const { initialState } = useModel('@@initialState');
  return initialState?.currentUser?.Integrations;
}

export function useServiceRequestPermissions(
  serviceRequest: ServiceRequestModel,
) {
  const { initialState } = useModel('@@initialState');
  const hasServiceRequestActionAll = useHasAccess([
    PERMISSIONS.ServiceRequests.ACTIONS_ALL,
    PERMISSIONS.BUSINESS_PARTNER,
  ]);
  const hasEditAccess = useHasAccess([
    PERMISSIONS.ServiceRequests.EDIT,
    PERMISSIONS.BUSINESS_PARTNER,
  ]);

  const isOwner =
    getServiceRequestAssignee(serviceRequest)?.id ===
    initialState?.currentUser?.User?.alias;

  const canPerformAction = isOwner || hasServiceRequestActionAll;
  const canEditServiceRequest = hasEditAccess || isOwner;

  return {
    isOwner,
    hasServiceRequestActionAll,
    canPerformAction,
    canEditServiceRequest,
  };
}

export function useDefaultLeadSourceConfiguration() {
  const dispatch = useDispatch();
  const { initialState } = useModel('@@initialState');
  const leadSources = useSelector(JourneysSelectors.selectJourneys);
  let defaultLeadSource = leadSources.find(
    (leadSource) => leadSource.is_default,
  );
  if (!defaultLeadSource) {
    defaultLeadSource = leadSources.filter(
      (leadSource) => leadSource.type === 'journey',
    )?.[0];
  }

  const defaultLeadSourceConfiguration = useSelector(
    (state: DefaultRootState) =>
      JourneysSelectors.selectJourneyConfiguration(
        state,
        defaultLeadSource?.hashId,
      ),
  );

  /**
   * `journey/fetchConfig` reads `GET /journeys/{hash}/` on the legacy API, which is
   * business-partner-only — `DvaWatcher` gates the identical dispatch on exactly
   * that. Ungated here it was a 403 for every granular role, four times in one
   * session, after which this hook handed its consumers an empty configuration with
   * no signal that it had failed. Same gate, so the request is only made by someone
   * allowed to make it.
   */
  const canReadJourneyConfig = hasAccess(
    initialState?.currentUser,
    PERMISSIONS.BUSINESS_PARTNER,
  );

  useEffect(() => {
    if (!canReadJourneyConfig) return;
    if (!defaultLeadSource?.hashId || !isEmpty(defaultLeadSourceConfiguration))
      return;
    dispatch({
      type: 'journey/fetchConfig',
      payload: { journeyId: defaultLeadSource?.hashId },
    });
  }, [canReadJourneyConfig, defaultLeadSource?.hashId]);

  return defaultLeadSourceConfiguration;
}
